import type { PropertyDto } from '@str/api/types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

const KEY = 'strl.activeProperty';
export function rememberActiveProperty(id: string) {
  try {
    localStorage.setItem(KEY, id);
  } catch {
    /* private mode */
  }
}
type DraftGuard = { save: () => Promise<void>; discard: () => void | Promise<void>; busy: boolean };
type Store = {
  properties: PropertyDto[];
  active: PropertyDto;
  setActive: (id: string) => Promise<boolean>;
  registerDraft: (draft: DraftGuard | null) => () => void;
};
const Ctx = createContext<Store | null>(null);

/**
 * `requested` is the property a link names (`?property=<id>`, e.g. from an email): when it is one
 * the host can see it wins over the remembered one, before anything renders, and is remembered.
 */
export function PropertyProvider({
  properties,
  requested = null,
  children,
}: {
  properties: PropertyDto[];
  requested?: string | null;
  children: ReactNode;
}) {
  const [activeId, setActiveId] = useState<string | null>(() => {
    if (requested && properties.some((p) => p.id === requested)) {
      rememberActiveProperty(requested);
      return requested;
    }
    try {
      return localStorage.getItem(KEY);
    } catch {
      return null;
    }
  });
  const guard = useRef<DraftGuard | null>(null);
  const [pending, setPending] = useState<{ id: string; resolve: (ok: boolean) => void } | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = properties.find((p) => p.id === activeId) ?? properties[0]!;
  const registerDraft = useCallback((draft: DraftGuard | null) => {
    guard.current = draft;
    return () => {
      if (guard.current === draft) guard.current = null;
    };
  }, []);
  const setActive = useCallback(
    async (id: string) => {
      if (id === active.id) return false;
      if (guard.current) {
        setError(null);
        return new Promise<boolean>((resolve) => setPending({ id, resolve }));
      }
      setActiveId(id);
      rememberActiveProperty(id);
      return true;
    },
    [active.id],
  );
  const finish = async (choice: 'save' | 'discard' | 'stay') => {
    if (!pending) return;
    if (choice === 'stay') {
      pending.resolve(false);
      setPending(null);
      return;
    }
    if (guard.current?.busy) {
      setError('Wait for the current edit to finish, then try again.');
      return;
    }
    setSaving(true);
    try {
      if (choice === 'save') await guard.current?.save();
      else await guard.current?.discard();
      setActiveId(pending.id);
      rememberActiveProperty(pending.id);
      pending.resolve(true);
      setPending(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the draft. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  // A link followed while the app is already open switches through the same draft guard.
  useEffect(() => {
    if (requested && requested !== active.id && properties.some((p) => p.id === requested))
      void setActive(requested);
    // Only a newly requested property switches; later picks in the app are the host's own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested]);
  const value = useMemo(
    () => ({ properties, active, setActive, registerDraft }),
    [properties, active, setActive, registerDraft],
  );
  return (
    <Ctx.Provider value={value}>
      {children}
      {pending && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: '#0005',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="switch-draft-title"
            className="card"
            style={{ padding: 24, maxWidth: 430, margin: 20 }}
          >
            <h2 id="switch-draft-title">Save your draft before switching?</h2>
            <p>Your draft belongs to {active.name}. Discard leaves its last saved version.</p>
            {error && <p role="alert">{error}</p>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <button
                className="btn btn-primary"
                disabled={saving}
                onClick={() => void finish('save')}
              >
                Save draft and switch
              </button>
              <button
                className="btn btn-ghost"
                disabled={saving}
                onClick={() => void finish('discard')}
              >
                Discard changes and switch
              </button>
              <button
                className="btn btn-ghost"
                disabled={saving}
                onClick={() => void finish('stay')}
              >
                Stay here
              </button>
            </div>
          </section>
        </div>
      )}
    </Ctx.Provider>
  );
}
export function useActiveProperty(): Store {
  const s = useContext(Ctx);
  if (!s) throw new Error('useActiveProperty must be used inside PropertyProvider');
  return s;
}
